import { ProductBehavior as ProductBehaviorEnum } from '@up2stay/up2promo-api-client';
import {
  buildInstagramTargetUrl,
  buildTiktokTargetUrl,
  type LocationRoutingWarning,
  type ProductFormLocation,
  type ProductFormValues,
  type ProductRoutingMode,
} from '@up2stay/up2promo-commons/products';
import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';

import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import type { ProductsDict } from '@/routes/app/products/products.types';

import { ProductFormLocationField } from './location-field';
import { ProductFormProfileUserField } from './profile-user-field';
import { ProductFormTargetUrlField } from './target-url-field';
import { ROUTING_MODE_I18N_KEYS } from './types';
import { ProductFormUsernameField } from './username-field';

export type ProductFormRoutingSectionProps = {
  values: ProductFormValues;
  onChange: (patch: Partial<ProductFormValues>) => void;
  knownProductType?: string | null;
  workspaceId?: string;
  locations: ProductFormLocation[];
  dict: ProductsDict | undefined;
  idPrefix: string;
  locationRequiredMessage?: string;
  hideProfileUserPicker?: boolean;
  lockedProfileUserLabel?: string;
  onCreateLocation?: () => void;
  createLocationLabel?: string;
  noLocationsTitle?: string;
  noLocationsDescription?: string;
  routing: {
    showRoutingModeSelector: boolean;
    showLegacyBanner: boolean;
    routingOptions: ProductRoutingMode[];
    routingMode: ProductRoutingMode;
    locationWarning: LocationRoutingWarning | null;
  };
  onRoutingModeChange: (mode: ProductRoutingMode) => void;
  instagramUsername: string;
  onInstagramUsernameChange: (username: string) => void;
  tiktokUsername: string;
  onTiktokUsernameChange: (username: string) => void;
  onTargetUrlBlur?: () => void;
  onTargetUrlFocus?: () => void;
  targetUrlErrorMessage?: string;
};

export function ProductFormRoutingSection({
  values,
  onChange,
  knownProductType,
  workspaceId,
  locations,
  dict,
  idPrefix,
  locationRequiredMessage,
  hideProfileUserPicker = false,
  lockedProfileUserLabel,
  onCreateLocation,
  createLocationLabel,
  noLocationsTitle,
  noLocationsDescription,
  routing,
  onRoutingModeChange,
  instagramUsername,
  onInstagramUsernameChange,
  tiktokUsername,
  onTiktokUsernameChange,
  onTargetUrlBlur,
  onTargetUrlFocus,
  targetUrlErrorMessage,
}: ProductFormRoutingSectionProps) {
  const routingDict = dict?.edit?.routing;
  const sectionTitle =
    routingDict?.title ?? dict?.edit?.routingTitle ?? 'Destination';

  const destinationField =
    routing.routingMode === 'profile' ? (
      hideProfileUserPicker ? (
        <div className="rounded-[var(--r-settings-group,12px)] bg-muted/30 px-3 py-2 text-[13.5px] leading-relaxed text-muted-foreground">
          {lockedProfileUserLabel ??
            dict?.setup?.profileSelf ??
            'Linked to your profile'}
        </div>
      ) : (
        <ProductFormProfileUserField
          workspaceId={workspaceId}
          value={values.userId}
          onChange={(userId) => onChange({ userId })}
          dict={dict}
          required
        />
      )
    ) : routing.routingMode === 'location' ? (
      <ProductFormLocationField
        value={values.locationId}
        onChange={(locationId) => onChange({ locationId })}
        locations={locations}
        dict={dict}
        knownProductType={knownProductType}
        required
        requiredMessage={locationRequiredMessage}
        locationWarning={routing.locationWarning}
        warningMessages={routingDict}
        onCreateLocation={onCreateLocation}
        createLocationLabel={createLocationLabel}
        noLocationsTitle={noLocationsTitle}
        noLocationsDescription={noLocationsDescription}
      />
    ) : routing.routingMode === 'instagramUsername' ? (
      <ProductFormUsernameField
        id={`${idPrefix}-instagram-username`}
        value={instagramUsername}
        onChange={(username) => {
          onInstagramUsernameChange(username);
          onChange({
            targetKind: ProductBehaviorEnum.URL,
            locationId: null,
            targetUrl: buildInstagramTargetUrl(username),
          });
        }}
        label={routingDict?.instagramUsernameLabel}
        placeholder={routingDict?.instagramUsernamePlaceholder ?? '@username'}
      />
    ) : routing.routingMode === 'tiktokUsername' ? (
      <ProductFormUsernameField
        id={`${idPrefix}-tiktok-username`}
        value={tiktokUsername}
        onChange={(username) => {
          onTiktokUsernameChange(username);
          onChange({
            targetKind: ProductBehaviorEnum.URL,
            locationId: null,
            targetUrl: buildTiktokTargetUrl(username),
          });
        }}
        label={routingDict?.tiktokUsernameLabel}
        placeholder={routingDict?.tiktokUsernamePlaceholder ?? '@username'}
      />
    ) : routing.routingMode === 'customUrl' ? (
      <ProductFormTargetUrlField
        id={`${idPrefix}-targetUrl`}
        value={values.targetUrl}
        onChange={(targetUrl) => onChange({ targetUrl })}
        onBlur={onTargetUrlBlur}
        onFocus={onTargetUrlFocus}
        label={dict?.edit?.customLink}
        hint={routingDict?.customUrlHint}
        required
        errorMessage={targetUrlErrorMessage}
      />
    ) : routing.routingMode === 'behaviourSelect' &&
      values.targetKind !== ProductBehaviorEnum.URL &&
      values.targetKind !== ProductBehaviorEnum.PROFILE ? (
      <ProductFormLocationField
        value={values.locationId}
        onChange={(locationId) => onChange({ locationId })}
        locations={locations}
        dict={dict}
        targetKindForReadiness={values.targetKind}
        required={
          values.targetKind === ProductBehaviorEnum.GOOGLE_REVIEW ||
          values.targetKind === ProductBehaviorEnum.INSTAGRAM ||
          values.targetKind === ProductBehaviorEnum.FACEBOOK ||
          values.targetKind === ProductBehaviorEnum.TIKTOK ||
          values.targetKind === ProductBehaviorEnum.MENU
        }
        requiredMessage={locationRequiredMessage}
        onCreateLocation={onCreateLocation}
        createLocationLabel={createLocationLabel}
        noLocationsTitle={noLocationsTitle}
        noLocationsDescription={noLocationsDescription}
      />
    ) : routing.routingMode === 'behaviourSelect' &&
      values.targetKind === ProductBehaviorEnum.URL ? (
      <ProductFormTargetUrlField
        id={`${idPrefix}-targetUrl`}
        value={values.targetUrl}
        onChange={(targetUrl) => onChange({ targetUrl })}
        onBlur={onTargetUrlBlur}
        onFocus={onTargetUrlFocus}
        label={dict?.edit?.customLink}
        required
        errorMessage={targetUrlErrorMessage}
      />
    ) : routing.routingMode === 'behaviourSelect' &&
      values.targetKind === ProductBehaviorEnum.PROFILE ? (
      <ProductFormProfileUserField
        workspaceId={workspaceId}
        value={values.userId}
        onChange={(userId) => onChange({ userId })}
        dict={dict}
        allowNone
      />
    ) : null;

  const legacyBanner =
    routing.showLegacyBanner && routingDict?.legacyUrl ? (
      <Alert variant="warning" className="rounded-md px-3 py-2">
        <AlertDescription className="text-base leading-snug md:text-sm">
          {routingDict.legacyUrl}
        </AlertDescription>
      </Alert>
    ) : null;

  return (
    <div className="flex flex-col gap-2">
      <SettingsGroupRows>
        {routing.showRoutingModeSelector ? (
          <SettingsGroupRow>
            <div
              className="flex flex-col gap-1.5"
              role="radiogroup"
              aria-label={sectionTitle}
            >
              {routing.routingOptions.map((mode) => {
                const labelKey = ROUTING_MODE_I18N_KEYS[mode];
                const label =
                  (routingDict?.[labelKey as keyof typeof routingDict] as
                    | string
                    | undefined) ??
                  (dict?.edit?.[labelKey as keyof typeof dict.edit] as
                    | string
                    | undefined) ??
                  mode;
                const selected = routing.routingMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => onRoutingModeChange(mode)}
                    className={cn(
                      'rounded-[11px] border px-3 py-2.5 text-left transition-colors',
                      selected
                        ? 'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] ring-1 ring-[color:var(--brand-500)]/30'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <span className="text-[13.5px] font-medium leading-snug">
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </SettingsGroupRow>
        ) : null}
        {destinationField || legacyBanner ? (
          <SettingsGroupRow
            separated={routing.showRoutingModeSelector}
            className={
              destinationField && legacyBanner
                ? 'flex flex-col gap-2'
                : undefined
            }
          >
            {destinationField}
            {legacyBanner}
          </SettingsGroupRow>
        ) : null}
      </SettingsGroupRows>
    </div>
  );
}
